import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { CreditCard, ExternalLink, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import { paymentMethodsApi } from '@/api/paymentMethods'
import { Loading } from '@/components/ui'

interface Props {
  orgId: string
}

const POPUP_NAME = 'jumpstart-sumit-card-vault'
const POPUP_FEATURES = 'popup=yes,width=560,height=760,resizable=yes,scrollbars=yes'

function formatSavedDate(value: string): string {
  const date = new Date(value)
  if (Number.isNaN(date.getTime())) return value
  return new Intl.DateTimeFormat('en-GB', {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
  }).format(date)
}

function errorMessage(error: unknown): string {
  return error instanceof Error ? error.message : 'Something went wrong. Please try again.'
}

export default function PaymentMethodBlock({ orgId }: Props) {
  const queryClient = useQueryClient()
  const queryKey = useMemo(() => ['admin-payment-method', orgId] as const, [orgId])
  const popupRef = useRef<Window | null>(null)
  const popupOriginRef = useRef<string | null>(null)
  const [popupBlocked, setPopupBlocked] = useState(false)
  const [confirmingRemoval, setConfirmingRemoval] = useState(false)

  const statusQuery = useQuery({
    queryKey,
    queryFn: () => paymentMethodsApi.status(orgId),
    staleTime: 30_000,
  })

  const clearPopup = useCallback((close = false) => {
    const popup = popupRef.current
    if (close && popup && !popup.closed) popup.close()
    popupRef.current = null
    popupOriginRef.current = null
  }, [])

  const refreshStatus = useCallback(async () => {
    await queryClient.invalidateQueries({ queryKey })
  }, [queryClient, queryKey])

  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      const popup = popupRef.current
      const expectedOrigin = popupOriginRef.current
      if (!popup || !expectedOrigin || event.source !== popup || event.origin !== expectedOrigin) return
      if (!event.data || typeof event.data !== 'object' || event.data.type !== 'jumpstart:sumit-vault') return

      clearPopup(true)
      void refreshStatus()
    }

    window.addEventListener('message', handleMessage)
    return () => window.removeEventListener('message', handleMessage)
  }, [clearPopup, refreshStatus])

  useEffect(() => {
    const timer = window.setInterval(() => {
      if (document.visibilityState==='visible'&&popupRef.current?.closed) {
        clearPopup()
        void refreshStatus()
      }
    }, 500)

    return () => {
      window.clearInterval(timer)
      clearPopup(true)
    }
  }, [clearPopup, refreshStatus])

  const beginMutation = useMutation({
    mutationFn: () => paymentMethodsApi.begin(orgId),
    onSuccess: ({ url }) => {
      const popup = popupRef.current
      if (!popup || popup.closed) {
        clearPopup()
        setPopupBlocked(true)
        return
      }

      try {
        popupOriginRef.current = new URL(url).origin
        popup.location.replace(url)
        popup.focus()
      } catch {
        clearPopup(true)
        toast.error('The secure payment page returned an invalid URL.')
      }
    },
    onError: (error) => {
      clearPopup(true)
      toast.error(errorMessage(error))
    },
  })

  const removeMutation = useMutation({
    mutationFn: () => paymentMethodsApi.remove(orgId),
    onSuccess: async () => {
      setConfirmingRemoval(false)
      await refreshStatus()
      toast.success('Saved card removed.')
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  const openVault = () => {
    setPopupBlocked(false)
    const popup = window.open('about:blank', POPUP_NAME, POPUP_FEATURES)
    if (!popup) {
      setPopupBlocked(true)
      return
    }

    popupRef.current = popup
    popupOriginRef.current = null
    popup.document.title = 'Opening secure card setup…'
    popup.document.body.textContent = 'Opening secure card setup…'
    beginMutation.mutate()
  }

  const status = statusQuery.data

  return (
    <div className="card border border-navy-border bg-navy-card p-6">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <CreditCard className="h-4 w-4 text-indigo-400" aria-hidden="true" />
            <h3 className="text-sm font-semibold text-ink-primary">Payment method</h3>
          </div>
          <p className="mt-1 text-xs text-ink-secondary">
            Card details are entered and stored securely on SUMIT.
          </p>
        </div>

        {!statusQuery.isLoading && !statusQuery.isError && (
          <button
            type="button"
            onClick={openVault}
            disabled={beginMutation.isPending}
            className="inline-flex h-9 items-center gap-2 rounded bg-indigo-600 px-3 text-sm font-medium text-ink-primary transition-colors hover:bg-indigo-500 disabled:pointer-events-none disabled:opacity-50"
          >
            <ExternalLink className="h-4 w-4" aria-hidden="true" />
            {beginMutation.isPending ? 'Opening…' : status?.has_payment_method ? 'Replace card' : 'Add card'}
          </button>
        )}
      </div>

      <div className="mt-5 border-t border-navy-border pt-4">
        {statusQuery.isLoading ? (
          <Loading text="Loading payment method…" />
        ) : statusQuery.isError ? (
          <div className="space-y-3">
            <p className="text-sm text-ink-secondary">{errorMessage(statusQuery.error)}</p>
            <button
              type="button"
              onClick={() => void statusQuery.refetch()}
              className="text-sm font-medium text-indigo-400 hover:text-indigo-300"
            >
              Try again
            </button>
          </div>
        ) : status?.has_payment_method ? (
          <div className="flex flex-wrap items-center justify-between gap-4">
            <div>
              <p className="font-medium text-ink-primary">
                ···· {status.last4 ?? '—'}
                {status.brand ? ` · ${status.brand}` : ''}
                {status.saved_at ? ` · saved ${formatSavedDate(status.saved_at)}` : ''}
              </p>
              <p className="mt-1 text-xs text-ink-secondary">Card on file</p>
            </div>

            {confirmingRemoval ? (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm text-ink-secondary">Remove this saved card?</span>
                <button
                  type="button"
                  onClick={() => removeMutation.mutate()}
                  disabled={removeMutation.isPending}
                  className="rounded border border-navy-border px-3 py-1.5 text-sm font-medium text-ink-primary transition-colors hover:bg-indigo-600 disabled:pointer-events-none disabled:opacity-50"
                >
                  {removeMutation.isPending ? 'Removing…' : 'Confirm'}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingRemoval(false)}
                  disabled={removeMutation.isPending}
                  className="px-2 py-1.5 text-sm text-ink-secondary hover:text-ink-primary disabled:pointer-events-none disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            ) : (
              <button
                type="button"
                onClick={() => setConfirmingRemoval(true)}
                className="inline-flex items-center gap-1.5 text-sm text-ink-secondary transition-colors hover:text-ink-primary"
              >
                <Trash2 className="h-4 w-4" aria-hidden="true" />
                Remove
              </button>
            )}
          </div>
        ) : (
          <p className="text-sm text-ink-secondary">No card on file.</p>
        )}

        {popupBlocked && (
          <p className="mt-3 text-sm text-ink-secondary">
            The secure card window was blocked. Allow popups for this site, then try again.
          </p>
        )}
        {beginMutation.isError && (
          <p className="mt-3 text-sm text-ink-secondary">{errorMessage(beginMutation.error)}</p>
        )}
      </div>
    </div>
  )
}
